import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

import API from './apiConfig';

export default function ResetPasswordPage({ onLogin }) {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) setToken(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); return; }
    if (password !== confirm) { setError('Passwörter stimmen nicht überein'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Zurücksetzen.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-zinc-300 font-mono text-sm">Ungültiger Reset-Link. Kein Token gefunden.</p>
          <button onClick={onLogin} className="mt-4 text-blue-400 hover:text-blue-300 text-sm font-mono">
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 relative">
      <button onClick={onLogin} data-testid="reset-back-btn"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-200 text-sm font-mono transition-colors">
        <ArrowLeft className="w-4 h-4" /> Zurück zum Login
      </button>
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="LAIzyBlueprint" className="h-14 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white font-mono tracking-wider">Neues Passwort</h1>
        </div>

        {success ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-7 h-7 text-green-400" />
            </div>
            <p className="text-zinc-300 text-sm font-mono">Passwort erfolgreich geändert!</p>
            <button onClick={onLogin} data-testid="reset-to-login"
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm rounded-lg transition-colors">
              Jetzt anmelden
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}
            <div className="relative">
              <label className="block text-xs text-zinc-500 mb-1 font-mono">Neues Passwort (min. 8 Zeichen)</label>
              <input data-testid="reset-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none pr-10" />
              <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-3 top-7 text-zinc-500 hover:text-zinc-300">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1 font-mono">Passwort bestätigen</label>
              <input data-testid="reset-confirm" type={showPw ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} required
                className="w-full px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm font-mono focus:border-blue-500 focus:outline-none" />
            </div>
            <button data-testid="reset-submit" type="submit" disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white font-mono text-sm rounded-lg transition-colors flex items-center justify-center gap-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Speichern...</> : 'Passwort ändern'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
